<template>
  <footer class="relative bg-slate-900 text-slate-300 font-sans overflow-hidden">
    
    <div class="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-600 via-blue-500 to-indigo-600"></div>

    <div class="absolute inset-0 pointer-events-none opacity-[0.03]" 
         style="background-image: linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px); background-size: 32px 32px;">
    </div>

    <div v-if="isEditMode" class="relative z-20 max-w-7xl mx-auto px-4 pt-6 flex justify-end">
      <button @click="emitEdit"
        class="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-900 bg-white rounded-full hover:bg-brand-500 hover:text-white transition-all shadow-lg">
        <i class="fas fa-pen-to-square"></i> Éditer le footer
      </button>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-8 lg:pt-20">
      
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
        
        <div class="lg:col-span-5 flex flex-col space-y-6">
          <div>
            <h3 class="text-3xl font-black tracking-tighter text-white mb-4">
              {{ main?.titre || 'Anjah R.' }}<span class="text-brand-500">.</span>
            </h3>
            <p class="text-slate-400 leading-relaxed max-w-sm">
              {{ main?.description || 'Votre partenaire expert en transformation numérique, propulsant vos projets vers de nouveaux sommets.' }}
            </p>
          </div>

          <div class="flex gap-3 pt-2">
            <a v-for="s in socialsNormalized" :key="s.id" :href="s.href" target="_blank" rel="noopener"
               class="h-10 w-10 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:bg-white hover:text-brand-600 hover:-translate-y-1 transition-all duration-300"
               :aria-label="'Suivre sur ' + s.id">
              <i :class="s.iconClass"></i>
            </a>
          </div>
        </div>

        <div class="lg:col-span-3">
          <h4 class="text-sm font-bold uppercase tracking-widest text-white mb-6">Exploration</h4>
          <ul class="space-y-3">
            <li v-for="l in linksNormalized" :key="l.id">
              <component :is="l.internal ? 'router-link' : 'a'" 
                         :to="l.internal ? l.href : undefined"
                         :href="!l.internal ? l.href : undefined" 
                         :target="!l.internal ? '_blank' : undefined"
                         class="group flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors duration-200">
                <span class="h-px w-0 bg-brand-500 group-hover:w-3 transition-all duration-300"></span>
                <span>{{ l.label || 'Lien' }}</span>
              </component>
            </li>
          </ul>
        </div>

        <div class="lg:col-span-4">
          <h4 class="text-sm font-bold uppercase tracking-widest text-white mb-6">Contact</h4>
          <div class="flex flex-col gap-5">
            <a v-for="c in contactWithLinks" :key="c.id" :href="c.link"
               :target="c.type === 'address' ? '_blank' : undefined"
               class="group flex items-start gap-4">
              
              <div class="shrink-0 h-10 w-10 rounded-lg bg-slate-800 flex items-center justify-center text-brand-500 group-hover:bg-brand-600 group-hover:text-white transition-colors duration-300">
                <i :class="c.iconClass" class="text-sm"></i>
              </div>
              
              <div class="flex-1 pt-1">
                <p class="text-xs font-bold text-slate-500 uppercase mb-0.5">{{ c.labelDisplay }}</p>
                <p class="text-sm font-medium text-slate-300 group-hover:text-white transition-colors break-words">
                  {{ c.value || 'Information indisponible' }}
                </p>
              </div>
            </a>
          </div>
        </div>

      </div>

      <div class="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
        <p class="text-xs text-slate-500 text-center md:text-left">
          {{ note?.description || '© 2025 Anjah R. Tous droits réservés.' }}
        </p>
        
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-2 px-3 py-1 rounded bg-slate-800/50 border border-slate-700">
            <div class="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Systems Normal</span>
          </div>
        </div>
      </div>

    </div>
  </footer>
</template>

<script>
// LE SCRIPT RESTE 100% IDENTIQUE (Logique intacte)
import http from "@/api/http";

export default {
  name: "FooterComponent",
  props: { isEditMode: { type: Boolean, default: false } },
  data() {
    return {
      main: null,
      links: [],
      socials: [],
      contact: [],
      note: null,
      loading: false,
    };
  },
  computed: {
    currentLang() {
      const fromStore = this.$store?.state?.langue;
      const fromRoute = this.$route?.params?.langue;
      const lang = fromStore || fromRoute || "fr";
      return ["fr", "en"].includes(lang) ? lang : "fr";
    },
    linksNormalized() {
      return (this.links || []).map((l) => {
        const raw = (l.description || "").trim();
        const isExternal = /^https?:\/\//i.test(raw);
        const href = isExternal ? raw : this.localizePath(raw);
        return { id: l.id, label: l.titre || "Lien", href, internal: !isExternal };
      });
    },
    socialsNormalized() {
      return (this.socials || []).map((s) => {
        const key = (s.titre || "").toLowerCase().trim();
        return { id: s.id, href: s.description || "#", iconClass: this.mapSocialIcon(key) };
      });
    },
    contactWithLinks() {
      return (this.contact || []).map((c) => {
        const type = (c.titre || "").toLowerCase().trim();
        const value = c.description || "";

        let link = "#";
        let labelDisplay = "Info";

        if (type === 'email') {
          link = `mailto:${value}`;
          labelDisplay = "Email";
        } else if (type === 'phone') {
          const cleanPhone = value.replace(/[^\d+]/g, '');
          link = `tel:${cleanPhone}`;
          labelDisplay = "Téléphone";
        } else if (type === 'address') {
          link = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`;
          labelDisplay = "Adresse";
        }

        return {
          id: c.id,
          type,
          value,
          link,
          labelDisplay,
          iconClass: this.mapContactIcon(type)
        };
      });
    }
  },
  watch: {
    currentLang() { this.fetchFooter(); },
  },
  mounted() {
    this.fetchFooter();
  },
  methods: {
    async fetchFooter() {
      try {
        this.loading = true;
        const { data } = await http.get("footer-content", { params: { langue: this.currentLang } });
        this.main = data.main;
        this.links = data.links;
        this.socials = data.socials;
        this.contact = data.contact;
        this.note = data.note;
      } catch (e) {
        console.error("Erreur footer:", e);
      } finally {
        this.loading = false;
      }
    },
    localizePath(raw) {
      let p = raw.startsWith("/") ? raw : `/${raw}`;
      if (/^\/(fr|en)\//i.test(p)) return p.replace(/^\/(fr|en)\//i, `/${this.currentLang}/`);
      return `/${this.currentLang}${p === '/' ? '' : p}`;
    },
    mapSocialIcon(key) {
      const map = {
        linkedin: 'fa-brands fa-linkedin-in',
        facebook: 'fa-brands fa-facebook-f',
        instagram: 'fa-brands fa-instagram',
        twitter: 'fa-brands fa-x-twitter',
        x: 'fa-brands fa-x-twitter',
        youtube: 'fa-brands fa-youtube',
        tiktok: 'fa-brands fa-tiktok',
        github: 'fa-brands fa-github'
      };
      return map[key] || 'fa-solid fa-globe';
    },
    mapContactIcon(type) {
      const map = {
        address: 'fa-solid fa-map-pin',
        email: 'fa-solid fa-at',
        phone: 'fa-solid fa-phone'
      };
      return map[type] || 'fa-solid fa-info';
    },
    emitEdit() {
      this.$emit("edit", {
        main: this.main,
        links: this.links,
        socials: this.socials,
        contact: this.contact,
        note: this.note,
        langue: this.currentLang,
      });
    },
  },
};
</script>

<style scoped>
/* Petit fix pour l'accessibilité sur fond sombre */
:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 4px;
}
</style>